<template>
  <div class="form-checkbox">
    <input
      :id="checkboxId"
      :checked="checked"
      type="checkbox"
      :aria-checked="checked"
      :aria-disabled="disabled"
      :tabindex="!disabled && 0"
      :class="['checkmark', disabled && 'disabled', checked && 'checked']"
      :style="orgColors"
      @change="onInputChange"
      @keypress.enter.prevent="onToggleKeypress"
      @keypress.space.prevent="onToggleKeypress"
    />
    <!-- eslint-disable-next-line vuejs-accessibility/label-has-for -->
    <label
      :for="checkboxId"
      class="checkbox-label"
    >
      <slot />
    </label>
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import { useMainStore } from '../../../stores/mainStore.js';
import tinycolor from 'tinycolor2';

defineOptions({ name: 'FormCheckbox' });

const props = defineProps({
  modelValue: Boolean,
  disabled: Boolean,
  checkboxId: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['changed']);

const mainStore = useMainStore();

const checked = ref(props.modelValue);

watch(
  () => props.modelValue,
  (newVal) => {
    checked.value = newVal;
  },
);

const accessibleCheckboxContrast = computed(() =>
  tinycolor.isReadable(mainStore.globals?.info?.primary_color?.hex, '#ffffff', {}),
);

const orgColors = computed(() => ({
  '--primary-color': accessibleCheckboxContrast.value ? mainStore.globals?.info?.primary_color?.hex : '#662e80',
}));

function handleSelection(value) {
  checked.value = value;
  emit('changed', { checked: checked.value });
}

function onInputChange(event) {
  handleSelection(event.target.checked);
}

function onToggleKeypress(event) {
  handleSelection(!event.target.checked);
}
</script>

<style scoped lang="scss">
@use '../scss/color_vars';

.form-checkbox {
  --checkbox-error-color: #{color_vars.$color-grey-50};
  --checkbox-background-color: #{color_vars.$color-primary-white};
  --checkbox-border-color: #{color_vars.$color-purple-40};
  --checkbox-selected-border-color: #{color_vars.$color-primary-purple};
  --checkbox-disabled-background-color: #{color_vars.$color-grey-20};
  --checkbox-disabled-border-color: #{color_vars.$color-grey-30};

  display: flex;
  align-items: center;
  margin-bottom: 6px;

  .checkmark {
    border: 1px solid;
    border-color: var(--checkbox-border-color);
    background: var(--checkbox-background-color);
    display: inline-block;
    width: 16px;
    height: 16px;
    border-radius: 2px;
    appearance: none;

    &:after {
      content: '';
      display: none;
      position: relative;
      left: 4px;
      top: 0;
      width: 4px;
      height: 9px;
      border: solid var(--checkbox-background-color);
      border-width: 0 2px 2px 0;
      -webkit-transform: rotate(45deg);
      -ms-transform: rotate(45deg);
      transform: rotate(45deg);
    }

    &.checked {
      width: 16px;
      height: 16px;
      border: 1px solid;
      border-color: var(--primary-color);
      background-color: var(--primary-color);

      &:after {
        display: block;
      }
    }

    &.disabled {
      width: 16px;
      height: 16px;
      background-color: var(--checkbox-disabled-background-color);
      border: 1px solid;
      border-color: var(--checkbox-disabled-border-color);
    }
  }

  .checkbox-label {
    margin-left: 8px;
  }
}
</style>
